import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaClock,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaMountain,
  FaPassport
} from 'react-icons/fa';

import './BhutanHimalayan.css';

const BhutanHimalayan = () => {

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: 'smooth'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const itineraryData = [
    {
      day: 1,
      title: "NJP / IXB to Phuentsholing",
      points: [
        "Arrival at NJP Railway Station or Bagdogra Airport.",
        "Meet & greet and transfer to Phuentsholing, the gateway to Bhutan.",
        "Upon arrival, check in to the hotel.",
        "Evening free for leisure or local market exploration.",
        "Dinner and overnight stay in Phuentsholing.",
        "Distance: Approx. 170 km"
      ]
    },

    {
      day: 2,
      title: "Phuentsholing to Thimphu",
      points: [
        "After breakfast, complete immigration formalities and proceed to Thimphu.",
        "En route visit Kharbandi Monastery.",
        "Visit Zangto Pelri Lhakhang.",
        "Enjoy scenic mountain views throughout the journey.",
        "Upon arrival, check in to the hotel.",
        "Dinner and overnight stay in Thimphu.",
        "Distance: Approx. 176 km | Travel Time: 6–7 hours"
      ]
    },

    {
      day: 3,
      title: "Thimphu to Punakha Excursion",
      points: [
        "Drive to Punakha via Dochu La Pass (3,150 meters).",
        "Visit Punakha Dzong.",
        "Walk across the Suspension Bridge.",
        "Visit Chhimi Lhakhang (Temple of Fertility).",
        "Return to Thimphu.",
        "Dinner and overnight stay in Thimphu.",
        "Distance: Approx. 150 km | Travel Time: 4–5 hours"
      ]
    },

    {
      day: 4,
      title: "Thimphu Sightseeing & Transfer to Paro",
      points: [
        "Visit Buddha Dordenma Statue.",
        "Visit National Memorial Chorten.",
        "Explore Folk Heritage Museum.",
        "Visit Sangaygang View Point.",
        "Visit Changangkha Monastery.",
        "Outer view of Tashichho Dzong.",
        "Transfer to Paro and hotel check-in.",
        "Dinner and overnight stay in Paro."
      ]
    },

    {
      day: 5,
      title: "Paro Sightseeing & Tiger’s Nest Hike",
      points: [
        "Visit Drukgyel Dzong.",
        "Visit Kyichu Lhakhang.",
        "Proceed for Taktsang Monastery (Tiger’s Nest) hike.",
        "Approx. 4–5 hours round trip.",
        "Pony ride available at additional cost.",
        "Return to hotel after hike.",
        "Dinner and overnight stay in Paro."
      ]
    },

    {
      day: 6,
      title: "Paro to Phuentsholing",
      points: [
        "Visit Paro Airport View Point.",
        "Explore local market area.",
        "Drive back to Phuentsholing.",
        "Hotel check-in on arrival.",
        "Dinner and overnight stay in Phuentsholing.",
        "Distance: Approx. 160 km | Travel Time: 6–7 hours"
      ]
    },

    {
      day: 7,
      title: "Phuentsholing to NJP / IXB",
      points: [
        "After breakfast, check out from hotel.",
        "Transfer to NJP Railway Station / Bagdogra Airport.",
        "Tour concludes with memorable experiences of Bhutan.",
        "Distance: Approx. 150 km | Travel Time: 4–5 hours"
      ]
    }
  ];

  return (

    <div className="trip-container">

      {/* HEADER */}

      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >

        <div className="header-left">

          <h1 className="trip-title">
            Bhutan Himalayan Escape
          </h1>

          <p className="trip-duration">
            6 Nights / 7 Days
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">
            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>
              <p>Phuentsholing, Thimphu, Punakha, Paro</p>
            </div>
          </div>

          <div className="info-row">
            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Start & End:</span>
              <p>NJP / Bagdogra Airport</p>
            </div>
          </div>

          <div className="info-row type-tag">
            <FaGlobeAmericas className="header-icon" />
            <p>Himalayan Cultural Journey</p>
          </div>

        </div>

      </motion.header>

      {/* IMAGE GRID */}

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1729176989417-10cab5aa9076?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Bhutan"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://images.unsplash.com/photo-1635134527208-6f514f0c2f0c?q=80&w=535&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Thimphu"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1761048152537-c20c8137e494?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Tiger Nest"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1746189130470-01f7f230e9fe?q=80&w=327&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Bhutan Mountains"
            />
          </div>

        </div>

      </motion.div>

      {/* STICKY NAV */}

      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}

        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (

              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">
                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>
                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (

                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>

                  ))}

                </div>

              </motion.div>

            ))}

          </div>

        </section>

        {/* HIGHLIGHTS */}

        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Scenic road journey into Bhutan</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit Dochu La Pass with Himalayan views</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Explore Thimphu city and monasteries</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit Punakha Dzong and Suspension Bridge</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Hike to Tiger’s Nest Monastery</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Experience Bhutanese culture and peaceful lifestyle</p>
            </div>

          </div>

        </motion.section>

        {/* INCLUSIONS */}

        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li><FaHotel /> Accommodation in hotels</li>

              <li><FaUtensils /> Daily breakfast and dinner</li>

              <li><FaBus /> Private vehicle transfers and sightseeing</li>

              <li><FaCheckCircle /> Immigration assistance</li>

              <li><FaShieldAlt /> Tour coordination and assistance</li>

              <li><FaCheckCircle /> One tourist SIM card per group</li>

            </ul>

          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li><FaTimesCircle /> Airfare or train tickets</li>

              <li><FaTimesCircle /> Bhutan SDF charges</li>

              <li><FaTimesCircle /> Bhutan guide charges</li>

              <li><FaTimesCircle /> Entry tickets</li>

              <li><FaTimesCircle /> Lunch and personal expenses</li>

              <li><FaTimesCircle /> Travel insurance</li>

            </ul>

          </div>

        </motion.section>

        {/* ABOUT */}

        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">

            Experience the serene beauty and cultural richness of Bhutan,
            a Himalayan kingdom known for monasteries, peaceful landscapes,
            and preserved traditions.

            <br /><br />

            This journey covers Phuentsholing, Thimphu, Punakha and Paro,
            offering scenic mountain drives, monasteries, cultural heritage,
            and the iconic Tiger’s Nest Monastery hike.

          </p>

        </motion.section>

        {/* IMPORTANT NOTES */}

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaPassport />
              Important Notes
            </h4>

            <ul className="p-list">

              <li>SDF charges applicable separately.</li>

              <li>Licensed Bhutan guide mandatory.</li>

              <li>Carry Passport or Voter ID for travel.</li>

              <li>Aadhaar and PAN Card are not accepted.</li>

            </ul>

          </div>

        </motion.section>

        {/* TERMS */}

        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

        {/* PRIVACY */}

        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

      </div>

    </div>
  );
};

export default BhutanHimalayan;